import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { getAuth, signOut } from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit {
  qrCodes: any[] = [];
  selectedQrCodes: Set<string> = new Set();
  tasks: { task: string; timestamp: any }[] = [];
  roundTimes: { time: string; timestamp: any }[] = [];
  guards: { name: string }[] = [];

  maxQrCodes = 0;
  selectedPlan: 'essentielle' | 'avancee' | 'serenite' = 'essentielle';
  lastReportUrl: string | null = null;
  lastReportName: string | null = null;

  isGenerating = false;
  isDownloadingZip = false;
  isDownloadingQr = false;
  isAccordionOpen = false;
  isTasksAccordionOpen = false;
  isGuardsAccordionOpen = false;
  isLoadingTasksAndRounds = true;
  isLoadingGuards = true;
  showUpgradePopup = false;

  constructor(private router: Router) {}

  async ngOnInit(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.warn('Utilisateur non connecté');
      this.router.navigate(['/se-connecter']);
      return;
    }

    await this.loadUserPlan();
    this.setMaxQrCodesFromPlan();
    await this.loadQrCodes();
    await this.loadTasksAndRounds();
    await this.loadGuards();
    await this.loadLastDailyReport();
  }

  // ================================
  // AUTHENTIFICATION
  // ================================
  logout(): void {
    signOut(getAuth()).then(() => this.router.navigate(['/se-connecter']));
  }

  // ================================
  // UI
  // ================================
  toggleAccordion(): void {
    this.isAccordionOpen = !this.isAccordionOpen;
  }
  toggleTasksAccordion(): void {
    this.isTasksAccordionOpen = !this.isTasksAccordionOpen;
  }
  toggleGuardsAccordion(): void {
    this.isGuardsAccordionOpen = !this.isGuardsAccordionOpen;
  }

  // ================================
  // PLAN UTILISATEUR
  // ================================
  private async loadUserPlan(): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) return;

    const userDoc = await getDoc(doc(getFirestore(), 'users', user.uid));
    if (userDoc.exists()) {
      this.selectedPlan = userDoc.data()['plan'] || 'essentielle';
    }
  }

  private setMaxQrCodesFromPlan(): void {
    this.maxQrCodes =
      this.selectedPlan === 'avancee' ? 14 : this.selectedPlan === 'serenite' ? 25 : 8;
  }

  // ================================
  // QR CODES
  // ================================
  async loadQrCodes(): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) return;

    const snapshot = await getDocs(collection(getFirestore(), `users/${user.uid}/qrCodes`));
    this.qrCodes = snapshot.docs
      .filter(docSnap => docSnap.id !== 'qrCodeStats')
      .map(docSnap => ({ ...docSnap.data(), folderName: docSnap.id }));

    await this.updateQrCodeCount(this.qrCodes.length);
  }

  private async updateQrCodeCount(count: number): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) return;

    const statsRef = doc(getFirestore(), `users/${user.uid}/qrCodes/qrCodeStats`);
    await setDoc(statsRef, { qrCodeCount: count, updatedAt: serverTimestamp() }, { merge: true });
  }

  generateRandomQrCode(): void {
    if (this.qrCodes.length >= this.maxQrCodes || this.isGenerating) {
      if (this.qrCodes.length >= this.maxQrCodes) this.showUpgradePopup = true;
      return;
    }

    const user = getAuth().currentUser;
    if (!user) return;

    const folderName = crypto.randomUUID();
    const qrCodeName = `QR ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
    const qrCodeData = `users/${user.uid}/${folderName}`;
    const generateQrCode = httpsCallable(getFunctions(), 'generateQrCode');

    this.isGenerating = true;
    generateQrCode({ folderName, qrCodeName, qrCodeData })
      .then(() => this.loadQrCodes())
      .catch(console.error)
      .finally(() => (this.isGenerating = false));
  }

  updateQrCodeName(qr: any): void {
    const user = getAuth().currentUser;
    if (!user) return;

    const qrDoc = doc(getFirestore(), `users/${user.uid}/qrCodes/${qr.folderName}`);
    updateDoc(qrDoc, { qrCodeName: qr.qrCodeName }).catch(console.error);
  }

  toggleSelection(folderName: string): void {
    this.selectedQrCodes.has(folderName)
      ? this.selectedQrCodes.delete(folderName)
      : this.selectedQrCodes.add(folderName);
  }

  isSelected(folderName: string): boolean {
    return this.selectedQrCodes.has(folderName);
  }

  async deleteQrCode(qr: any): Promise<void> {
    if (!window.confirm('Voulez-vous vraiment supprimer ce QR Code ?')) return;

    const user = getAuth().currentUser;
    if (!user) return;

    const deleteQrCodeFn = httpsCallable(getFunctions(), 'deleteQrCode');
    const folderPath = `users/${user.uid}/${qr.folderName}/qr_code.png`;

    try {
      await deleteQrCodeFn({ folderPath, documentId: qr.folderName });
      this.qrCodes = this.qrCodes.filter(q => q.folderName !== qr.folderName);
      await this.updateQrCodeCount(this.qrCodes.length);
    } catch (error) {
      console.error('Erreur suppression QR :', error);
    }
  }

  async deleteSelectedQrCodes(): Promise<void> {
    if (!window.confirm('Voulez-vous vraiment supprimer les QR Codes sélectionnés ?')) return;
    const toDelete = this.qrCodes.filter(qr => this.selectedQrCodes.has(qr.folderName));
    for (const qr of toDelete) await this.deleteQrCode(qr);
    this.selectedQrCodes.clear();
  }

  async downloadQrCode(qr: any): Promise<void> {
    this.isDownloadingQr = true;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const result: any = await httpsCallable(
      getFunctions(),
      'getQrCodeImage'
    )({
      folderPath: `users/${uid}/${qr.folderName}/qr_code.png`,
      documentId: qr.folderName,
    });

    await this.saveImageWithText(result.data.imageBytes, result.data.qrCodeName);
    this.isDownloadingQr = false;
  }

  async downloadSelectedQrCodes(): Promise<void> {
    this.isDownloadingZip = true;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const zip = new JSZip();
    for (const folderName of this.selectedQrCodes) {
      try {
        const result: any = await httpsCallable(
          getFunctions(),
          'getQrCodeImage'
        )({
          folderPath: `users/${uid}/${folderName}/qr_code.png`,
          documentId: folderName,
        });

        const canvas = await this.createLabeledCanvas(
          result.data.imageBytes,
          result.data.qrCodeName
        );
        const blob = await new Promise<Blob>(resolve =>
          canvas.toBlob(b => resolve(b!), 'image/png')
        );
        zip.file(`${result.data.qrCodeName}.png`, blob);
      } catch (error) {
        console.error('Erreur QR :', error);
      }
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `qr_codes_watchmann_${new Date().toISOString().split('T')[0]}.zip`);
    this.isDownloadingZip = false;
  }

  private async saveImageWithText(imageBytes: number[], name: string): Promise<void> {
    const canvas = await this.createLabeledCanvas(imageBytes, name);
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }, 'image/png');
  }

  private async createLabeledCanvas(
    imageBytes: number[],
    name: string
  ): Promise<HTMLCanvasElement> {
    const blob = new Blob([new Uint8Array(imageBytes)], { type: 'image/png' });
    const img = await this.loadImage(blob);
    const canvas = document.createElement('canvas');
    const scale = 3;
    const textHeight = 30;

    canvas.width = img.width * scale;
    canvas.height = img.height * scale + textHeight;

    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, canvas.width, img.height * scale);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, canvas.height - textHeight, canvas.width, textHeight);
    ctx.fillStyle = 'black';
    ctx.font = `${16 * scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, canvas.height - textHeight / 2);

    return canvas;
  }

  private loadImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // ================================
  // TÂCHES ET RONDES
  // ================================
  async loadTasksAndRounds(): Promise<void> {
    this.isLoadingTasksAndRounds = true;
    const user = getAuth().currentUser;
    if (!user) return;

    try {
      const taskSnap = await getDocs(collection(getFirestore(), `users/${user.uid}/tasks`));
      this.tasks = taskSnap.docs.map(doc => ({
        task: doc.data()['task'],
        timestamp: doc.data()['timestamp'],
      }));

      const roundSnap = await getDocs(collection(getFirestore(), `users/${user.uid}/roundTimes`));
      this.roundTimes = roundSnap.docs.map(doc => ({
        time: doc.data()['time'],
        timestamp: doc.data()['timestamp'],
      }));
    } catch (err) {
      console.error('Erreur tâches/rondes :', err);
    }

    this.isLoadingTasksAndRounds = false;
  }

  // ================================
  // RONDIERS
  // ================================
  async loadGuards(): Promise<void> {
    this.isLoadingGuards = true;
    const currentUserId = getAuth().currentUser?.uid;
    if (!currentUserId) return;

    try {
      const q = query(
        collection(getFirestore(), 'watchmen'),
        where('creatorId', '==', currentUserId)
      );
      const snapshot = await getDocs(q);
      this.guards = snapshot.docs.map(doc => ({
        name: `${doc.data()['prenom']} ${doc.data()['nom']}`,
      }));
    } catch (err) {
      console.error('Erreur chargement rondiers :', err);
    }

    this.isLoadingGuards = false;
  }

  // ================================
  // RAPPORTS QUOTIDIENS
  // ================================
  async loadLastDailyReport(): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) return;

    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        console.warn('[DEBUG] Document utilisateur introuvable');
        return;
      }

      const establishmentId = userDoc.data()['establishmentId'];
      if (!establishmentId) {
        console.warn('[DEBUG] Aucun establishmentId trouvé');
        return;
      }

      const storage = getStorage();
      const reportsRef = ref(storage, `establishments/${establishmentId}/daily-reports`);
      const result = await listAll(reportsRef);

      if (result.items.length > 0) {
        const sortedItems = result.items.sort((a, b) => {
          const dateA = a.name.match(/\d{4}-\d{2}-\d{2}/)?.[0] || '';
          const dateB = b.name.match(/\d{4}-\d{2}-\d{2}/)?.[0] || '';
          return dateB.localeCompare(dateA);
        });

        const lastReportRef = sortedItems[0];
        this.lastReportName = lastReportRef.name;
        this.lastReportUrl = await getDownloadURL(lastReportRef);
      } else {
        console.warn('[DEBUG] Aucun rapport trouvé');
        this.lastReportName = null;
        this.lastReportUrl = null;
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la récupération du dernier rapport:', error);
    }
  }

  async downloadLastReport(): Promise<void> {
    if (!this.lastReportUrl) {
      console.error('Aucun rapport disponible');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = this.lastReportUrl;
      link.download = this.lastReportName || 'rapport.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erreur téléchargement rapport :', error);
    }
  }

  // ================================
  // POPUP
  // ================================
  closePopup(): void {
    this.showUpgradePopup = false;
  }
}
